import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, XCircle, Sparkles, ArrowLeft, Plus, Filter } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_META = {
  overdue: { label: "Overdue", bg: "var(--alert-light)", fg: "var(--alert)", dot: "#dc2626" },
  due_soon: { label: "Due soon", bg: "var(--warning-light)", fg: "var(--warning)", dot: "#d97706" },
  upcoming: { label: "Upcoming", bg: "var(--slate-light)", fg: "var(--slate)", dot: "#64748b" },
  completed: { label: "Completed", bg: "var(--success-light)", fg: "var(--success)", dot: "#059669" },
  dismissed: { label: "Dismissed", bg: "var(--slate-light)", fg: "var(--tagline-grey)", dot: "#94a3b8" },
};
const SEV_DOT = { critical: "#dc2626", important: "#d97706", recommended: "#64748b" };
const CATEGORIES = ["meeting", "reporting", "financial", "insurance", "training", "safety", "records"];
const STATUSES = ["overdue", "due_soon", "upcoming", "completed", "dismissed"];

export function ComplianceList() {
  const navigate = useNavigate();
  const [scheme, setScheme] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    const id = getActiveSchemeId();
    if (!id) { navigate("/onboarding"); return; }
    try {
      const [s, ci] = await Promise.all([
        api.get(`/schemes/${id}`),
        api.get(`/schemes/${id}/compliance`),
      ]);
      setScheme(s.data);
      setItems(ci.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
      else setError(apiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const canWrite = scheme && (scheme.my_role === "chair" || scheme.my_role === "committee");
  const filtered = items.filter((i) =>
    (statusFilter === "all" || i.status === statusFilter) &&
    (catFilter === "all" || i.category === catFilter)
  );

  if (loading) return <AppShell><div className="min-h-[40vh] flex items-center justify-center"><span className="ai-dot" /><span className="ai-dot" /><span className="ai-dot" /></div></AppShell>;
  if (error) return <AppShell><div className="max-w-6xl mx-auto px-6 py-10"><div className="rounded-lg p-4 text-sm" style={{ background: "var(--alert-light)", color: "var(--alert)" }}>{error}</div></div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="compliance-list-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
              <Sparkles size={11} /> Compliance Radar · {scheme?.state}
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: "var(--navy)" }}>
              {scheme?.name} — compliance
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
              {items.length} tracked obligations. State-aware for {scheme?.state}.
            </p>
          </div>
          {canWrite && (
            <CreateCustomDialog open={createOpen} setOpen={setCreateOpen} schemeId={scheme.scheme_id} onCreated={load} />
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="compliance-filters">
          <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testid="filter-status-all">All</FilterChip>
          {STATUSES.map((s) => (
            <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} testid={`filter-status-${s}`}>
              {STATUS_META[s].label}
            </FilterChip>
          ))}
          <span className="mx-2 h-5 border-l" style={{ borderColor: "var(--border)" }} />
          <Filter size={12} style={{ color: "var(--slate)" }} />
          <FilterChip active={catFilter === "all"} onClick={() => setCatFilter("all")} testid="filter-cat-all">All categories</FilterChip>
          {CATEGORIES.map((c) => (
            <FilterChip key={c} active={catFilter === c} onClick={() => setCatFilter(c)} testid={`filter-cat-${c}`}>{c}</FilterChip>
          ))}
        </div>

        {/* Items */}
        <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm" style={{ color: "var(--tagline-grey)" }} data-testid="compliance-empty">
              {statusFilter === "overdue"
                ? "Nothing overdue — the committee is on top of it."
                : "No items match the current filters."}
            </div>
          ) : filtered.map((item) => (
            <Link
              key={item.item_id}
              to={`/scheme/compliance/${item.item_id}`}
              className="flex items-center gap-4 px-5 py-4 border-t hover:bg-slate-50 transition-colors first:border-t-0"
              style={{ borderColor: "var(--border)" }}
              data-testid={`compliance-row-${item.item_id}`}
            >
              <span className="inline-block w-2 h-2 rounded-full mt-1" style={{ background: SEV_DOT[item.severity] }} title={`Severity: ${item.severity}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" style={{ color: "var(--navy)" }}>{item.title}</div>
                <div className="text-xs mt-0.5 flex flex-wrap gap-x-3" style={{ color: "var(--slate)" }}>
                  <span className="uppercase tracking-wider">{item.category}</span>
                  <span className="mono">due {item.due_date}</span>
                  <span style={{ color: "var(--tagline-grey)" }}>{item.legislation_reference}</span>
                </div>
              </div>
              <StatusBadge status={item.status} />
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors"
      style={{
        borderColor: active ? "var(--blue)" : "var(--border)",
        background: active ? "var(--blue-light)" : "white",
        color: active ? "var(--blue-dark)" : "var(--slate)",
      }}>
      {children}
    </button>
  );
}

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.upcoming;
  return (
    <span data-testid={`status-badge-${status}`}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap"
      style={{ background: m.bg, color: m.fg }}>
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} />
      {m.label}
    </span>
  );
}

function CreateCustomDialog({ open, setOpen, schemeId, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("records");
  const [severity, setSeverity] = useState("important");
  const [dueDate, setDueDate] = useState("");
  const [legref, setLegref] = useState("general — verify with strata lawyer");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/schemes/${schemeId}/compliance`, {
        title, description, category, severity, legislation_reference: legref, due_date: dueDate,
      });
      toast.success("Item added");
      setOpen(false); setTitle(""); setDescription(""); setDueDate("");
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button style={{ background: "var(--blue)", color: "white" }} className="h-10 font-semibold" data-testid="add-custom-item-btn">
          <Plus size={14} className="mr-1.5" /> Add custom item
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add custom compliance item</DialogTitle>
          <DialogDescription>Track an obligation not covered by the standard templates.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="custom-title" /></Field>
          <Field label="Description"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="custom-desc" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Category">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="custom-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="custom-sev"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="recommended">Recommended</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Due date"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required data-testid="custom-due" /></Field>
          <Field label="Legislation reference"><Input value={legref} onChange={(e) => setLegref(e.target.value)} data-testid="custom-legref" /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} style={{ background: "var(--blue)", color: "white" }} data-testid="custom-submit">
              {loading ? "Creating…" : "Create item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>{label}</Label>
      {children}
    </div>
  );
}

// ============================================================================
// Detail page
// ============================================================================
export function ComplianceDetail() {
  const { item_id } = useParams();
  const navigate = useNavigate();
  const [scheme, setScheme] = useState(null);
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [notes, setNotes] = useState("");
  const [dismissReason, setDismissReason] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    const sid = getActiveSchemeId();
    if (!sid) { navigate("/onboarding"); return; }
    try {
      const [s, i] = await Promise.all([
        api.get(`/schemes/${sid}`),
        api.get(`/schemes/${sid}/compliance/${item_id}`),
      ]);
      setScheme(s.data); setItem(i.data);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(); }, [item_id]); // eslint-disable-line

  const [explainRateLimit, setExplainRateLimit] = useState(null);
  const [explainStreaming, setExplainStreaming] = useState(false);
  const [explainLive, setExplainLive] = useState("");

  const explain = async () => {
    setExplainLoading(true); setExplanation(null); setExplainRateLimit(null);
    setExplainStreaming(true); setExplainLive("");
    const { streamSSE } = await import("@/lib/sseStream");
    const token = localStorage.getItem("opstrai.session_token");
    await streamSSE({
      url: `${process.env.REACT_APP_BACKEND_URL}/api/ai/compliance/explain`,
      body: { item_id, scheme_id: scheme.scheme_id },
      headers: { Authorization: `Bearer ${token}` },
      onEvent: ({ event, data }) => {
        if (event === "token") { setExplainLoading(false); setExplainLive((p) => p + (data?.text || "")); }
        else if (event === "done") { setExplanation(data); setExplainStreaming(false); setExplainLoading(false); setExplainLive(""); }
        else if (event === "error") { toast.error(data?.message || "AI provider error"); setExplainStreaming(false); setExplainLoading(false); }
      },
      onError: (err) => {
        if (err?.status === 429) {
          const secs = parseInt(err?.retryAfter || "3600", 10);
          const mins = Math.max(1, Math.ceil(secs / 60));
          setExplainRateLimit(`AI request limit reached. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`);
        } else { toast.error(err?.message || "Failed"); }
        setExplainStreaming(false); setExplainLoading(false);
      },
    });
  };

  const complete = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/schemes/${scheme.scheme_id}/compliance/${item_id}/complete`, { notes: notes || null });
      setItem(data); toast.success("Marked as completed");
    } catch (err) { toast.error(apiError(err)); }
    finally { setActing(false); }
  };

  const dismiss = async () => {
    if (!dismissReason.trim()) { toast.error("Reason required"); return; }
    setActing(true);
    try {
      const { data } = await api.post(`/schemes/${scheme.scheme_id}/compliance/${item_id}/dismiss`, { reason: dismissReason });
      setItem(data); toast.success("Dismissed");
    } catch (err) { toast.error(apiError(err)); }
    finally { setActing(false); }
  };

  if (error) return <AppShell><div className="max-w-3xl mx-auto px-6 py-10"><div className="rounded-lg p-4 text-sm" style={{ background: "var(--alert-light)", color: "var(--alert)" }}>{error}</div></div></AppShell>;
  if (!item || !scheme) return <AppShell><div className="min-h-[40vh] flex items-center justify-center"><span className="ai-dot" /><span className="ai-dot" /><span className="ai-dot" /></div></AppShell>;

  const canAct = (scheme.my_role === "chair" || scheme.my_role === "committee") && item.status !== "completed" && item.status !== "dismissed";
  const canDismiss = scheme.my_role === "chair" && item.status !== "completed" && item.status !== "dismissed";

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8 lg:py-10 space-y-5" data-testid="compliance-detail-page">
        <Link to="/scheme/compliance" className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--blue)" }} data-testid="back-to-list">
          <ArrowLeft size={14} /> Back to Compliance Radar
        </Link>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: SEV_DOT[item.severity] }} />
            <span className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
              {item.category} · {item.severity}
            </span>
            <StatusBadge status={item.status} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }} data-testid="detail-title">{item.title}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>{item.description}</p>
          <div className="mt-3 text-xs flex flex-wrap gap-3" style={{ color: "var(--tagline-grey)" }}>
            <span className="mono">Due: {item.due_date}</span>
            <span>{item.legislation_reference}</span>
            <span>Recurrence: {item.recurrence}</span>
          </div>
        </div>

        {/* AI explain */}
        <div className="rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-bold" style={{ color: "var(--navy)" }}>Explain with AI</h2>
            <Button onClick={explain} disabled={explainLoading} variant="outline" data-testid="ai-explain-btn">
              <Sparkles size={14} className="mr-1.5" />{explainLoading ? "Thinking…" : "Explain"}
            </Button>
          </div>
          {explainStreaming && explainLive && !explanation && (
            <div className="rounded-md border p-3 mb-3" style={{ borderColor: "var(--blue)" }} data-testid="ai-explain-streaming">
              <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--blue)" }}>
                <span className="ai-dot"/> Streaming…
              </div>
              <pre className="whitespace-pre-wrap text-xs font-sans" style={{ color: "var(--navy)" }} data-testid="ai-explain-live">{explainLive}</pre>
            </div>
          )}
          {explanation ? (
            <div className="space-y-3 text-sm" data-testid="ai-explanation">
              <Section title="What it is">{explanation.plain_english}</Section>
              <Section title="Why it matters">{explanation.why_it_matters}</Section>
              <Section title="What happens if missed">{explanation.what_happens_if_missed}</Section>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--slate)" }}>Suggested next steps</div>
                <ul className="list-disc pl-5 space-y-0.5" style={{ color: "var(--navy)" }}>
                  {explanation.suggested_next_steps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <p className="text-[11px] italic pt-2 border-t" style={{ color: "var(--tagline-grey)", borderColor: "var(--border)" }}>
                {explanation.disclaimer}
              </p>
            </div>
          ) : (
            <p className="text-xs" style={{ color: "var(--tagline-grey)" }}>
              Click Explain to get a plain-English summary, consequences, and next steps (Claude Sonnet 4.5, state-aware).
            </p>
          )}
        </div>

        {/* Actions */}
        {canAct && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }}>
              <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5" style={{ color: "var(--success)" }}>
                <CheckCircle2 size={14} /> Mark complete
              </h3>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Completion notes (optional)" className="text-sm mb-2" data-testid="complete-notes" />
              <Button onClick={complete} disabled={acting} style={{ background: "var(--success)", color: "white" }} className="w-full font-semibold" data-testid="complete-btn">
                Complete
              </Button>
            </div>
            {canDismiss && (
              <div className="rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }}>
                <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5" style={{ color: "var(--alert)" }}>
                  <XCircle size={14} /> Dismiss
                </h3>
                <Textarea value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} placeholder="Reason (required) — e.g. not applicable to this scheme" className="text-sm mb-2" data-testid="dismiss-reason" />
                <Button onClick={dismiss} disabled={acting} variant="outline" className="w-full font-semibold" data-testid="dismiss-btn">
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}

        {item.status === "completed" && (
          <div className="rounded-lg p-3 text-sm flex items-start gap-2" style={{ background: "var(--success-light)", color: "var(--success)" }} data-testid="completed-note">
            <CheckCircle2 size={16} className="mt-0.5" />
            <div>Completed {item.completed_at?.slice(0, 10)}. {item.completed_notes && <span className="block mt-1 italic">"{item.completed_notes}"</span>}</div>
          </div>
        )}
        {item.status === "dismissed" && (
          <div className="rounded-lg p-3 text-sm flex items-start gap-2" style={{ background: "var(--slate-light)", color: "var(--slate)" }} data-testid="dismissed-note">
            <AlertCircle size={16} className="mt-0.5" />
            <div>Dismissed {item.dismissed_at?.slice(0, 10)} — <em>{item.dismissed_reason}</em></div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--slate)" }}>{title}</div>
      <p className="leading-relaxed" style={{ color: "var(--navy)" }}>{children}</p>
    </div>
  );
}
